import {
  ActionUpdateStatusRequest,
  BulkDeleteItemsRequest,
  CreatePatientEnrollmentRequest,
  DownloadRequest,
  EnrollmentEditUploadResponse,
  GetDashboardEmbedUrlSlugEnum,
  GetEnrollmentDashboardEmbedUrlSlugEnum,
  GetPatientEnrollmentActionsStatusEnum,
  GetSurgeriesProcedureTypeEnum,
  Patient,
  PatientCarePlanUploadRequest,
  PatientCarePlanUploadResponse,
  PatientPhoto,
  PatientPhotoUploadRequest,
  PatientPhotoUploadResponse,
  PendingPatientEnrollment,
  PredefinedAction,
  UploadRequest,
  UploadRequestUpload,
  UploadResponse,
  UserPreferences,
} from "@clients/velys-infrastructure/api";
import { velysInstance } from "./velys-api";
import fileUploadInstance from "./upload-api";
import { AxiosError } from "axios";

export const uploadRequest = async (
  orgId: string,
  uploadRequest: UploadRequest
) => {
  try {
    const response = await velysInstance.createUploadRequest(
      orgId,
      uploadRequest
    );
    if (response.status === 201 && response.data) {
      return response.data;
    }
  } catch (error) {
    console.error("Error creating upload request", error);
    throw error;
  }
};

export const uploadZipFile = async (
  uploadURI: string,
  file: File,
  uploadRequestObj: UploadRequestUpload,
  uploadResponseObj: UploadResponse,
  onProgress?: (percent: number) => void
) => {
  try {
    return await fileUploadInstance.put(uploadURI, file, {
      headers: {
        "Content-Type": file.type,
        ...uploadResponseObj.additional_headers,
      },
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          const percent = Math.round((event.loaded * 100) / event.total);
          onProgress(percent);
        }
      },
    });
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

export const downloadFiles = async (
  orgId: string,
  downloadRequest: DownloadRequest
) => {
  try {
    const response = await velysInstance.downloadFiles(orgId, downloadRequest);
    if (response.status === 200 && response.data) {
      return response.data;
    }
  } catch (error) {
    console.error("Error downloading files", error);
    throw error;
  }
};

export const downloadFilesLink = async (
  orgId: string,
  downloadId: string,
  downloadRequest: DownloadRequest
) => {
  try {
    const response = await velysInstance.downloadFilesLink(
      orgId,
      downloadId,
      downloadRequest
    );
    if (response.status === 200 && response.data) {
      return response.data;
    }
  } catch (error) {
    console.error("Error downloading files", error);
    throw error;
  }
};

export const getSurgeryById = async (orgId: string, patientId: string) => {
  try {
    const response = await velysInstance.getSurgery(orgId, patientId);
    if (response.status === 200 && response.data) {
      return response.data;
    }
  } catch (error) {
    console.error("Error getting patient", error);
    throw error;
  }
};

export const getSurgeries = async (
  orgId: string,
  q?: string,
  location?: string,
  procedureType?: GetSurgeriesProcedureTypeEnum,
  cursor?: string
) => {
  try {
    const response = await velysInstance.getSurgeries(
      orgId,
      q !== "" ? q : undefined,
      location !== "all" ? location : undefined,
      procedureType,
      cursor
    );
    if (response.status === 200 && response.data) {
      return {
        data: response.data,
        cursor: response.headers["x-next-page-cursor"],
      };
    }
  } catch (error) {
    console.error("Error getting patients", error);
    throw error;
  }
};

export const getMetricsUrl = async (
  orgId: string,
  slug: GetDashboardEmbedUrlSlugEnum
) => {
  try {
    const response = await velysInstance.getDashboardEmbedUrl(orgId, slug);
    if (response.status === 200 && response.data) {
      return response.data.embedUrl;
    }
  } catch (error) {
    console.error("Error getting dashboard url", error);
    throw error;
  }
};

export const getSurgeonMetrics = async (orgId: string, surgeon_npi: string) => {
  try {
    const response = await velysInstance.getSurgeonMetrics(orgId, surgeon_npi);
    if (response.status === 200 && response.data) {
      return response.data;
    }
  } catch (error) {
    console.error("Error getting surgeon metrics", error);
    throw error;
  }
};

export const getPatientPromsMetrics = async (
  orgId: string,
  enrollmentId: string
) => {
  try {
    const response = await velysInstance.getPatientPromsMetrics(
      orgId,
      enrollmentId
    );
    if (response.status === 200 && response.data) {
      return response.data;
    }
  } catch (error) {
    console.error("Error getting surgeon metrics", error);
    throw error;
  }
};

export const getPatientMetrics = async (
  enrollmentId: string,
  slug: GetEnrollmentDashboardEmbedUrlSlugEnum
) => {
  try {
    const response = await velysInstance.getEnrollmentDashboardEmbedUrl(
      enrollmentId,
      slug
    );
    if (response.status === 200 && response.data) {
      return response.data.embedUrl;
    }
  } catch (error) {
    console.error(
      "Error getting enrollment post-op metrics dashboard url",
      error
    );
    throw error;
  }
};

export const getOrgProfile = async (orgId: string) => {
  try {
    const response = await velysInstance.getOrg(orgId);
    if (response.status === 200 && response.data) {
      return response.data;
    }
  } catch (error) {
    console.error("Error getting org profile", error);
    throw error;
  }
};

// VPP V1

// get user profile
export const getUserProfile = async (userId: string) => {
  try {
    const response = await velysInstance.getUserProfile(userId);
    return response.data;
  } catch (error) {
    console.error("Error getting user profile", error);
    throw error;
  }
};

// get user preferences
export const getUserPreferences = async (userId: string) => {
  try {
    const response = await velysInstance.getUserPreferences(userId);
    return response.data;
  } catch (error) {
    console.error("Error getting user preferences", error);
    throw error;
  }
};

// update user preferences
export const updateUserPreferences = async (
  userId: string,
  userPreferences: UserPreferences
) => {
  try {
    const response = await velysInstance.updateUserPreferences(
      userId,
      userPreferences
    );
    return response.data;
  } catch (error) {
    console.error("Error updating user preferences", error);
    throw error;
  }
};

// get org notifications
export const getOrgNotifications = async (orgId: string, cusror?: string) => {
  try {
    const response = await velysInstance.getOrgNotifications(orgId, cusror);
    if (response.status === 200 && response.data) {
      return {
        data: response.data,
        nextCursor: response.headers["x-next-page-cursor"] as
          | string
          | undefined,
      };
    }
  } catch (error) {
    console.error("Error getting org notifications", error);
    throw error;
  }
};

// delete org notification
export const deleteOrgNotification = async (
  orgId: string,
  notificationId: string
) => {
  try {
    const response = await velysInstance.deleteOrgNotification(
      orgId,
      notificationId
    );
    if (response.status === 200) {
      return response;
    }
  } catch (error) {
    console.error("Error deleting notification:", error);
    throw error;
  }
};

// patient photo upload request: pre_signed_url
export const createPatientPhotoUpload = async (
  orgId: string,
  enrollmentId: string,
  patientPhotoUploadRequest: PatientPhotoUploadRequest
) => {
  try {
    const response =
      await velysInstance.organizationsOrgIdEnrollmentsEnrollmentIdUploadPost(
        orgId,
        enrollmentId,
        patientPhotoUploadRequest
      );
    if (response.status === 201 && response.data) {
      return response.data;
    }
  } catch (error) {
    console.error("Error creating upload request", error);
    throw error;
  }
};

// upload patient photo w/ pre_signed_url
export const uploadPatientPhoto = async (
  uploadURI: string,
  file: File,
  patientPhotoUploadResponse: PatientPhotoUploadResponse
) => {
  try {
    return await fileUploadInstance.put(uploadURI, file, {
      headers: {
        "Content-Type": file.type,
        ...patientPhotoUploadResponse.additional_headers,
      },
    });
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

// get patient enrollment photos
export const getEnrollmentPhotos = async (
  orgId: string,
  enrollmentId: string,
  cursor?: string
) => {
  try {
    const response = await velysInstance.listEnrollmentPhotos(
      orgId,
      enrollmentId,
      cursor
    );
    if (response.status === 200 && response.data) {
      return {
        data: response.data,
        cursor: response.headers["x-next-page-cursor"],
      };
    }
  } catch (error) {
    console.error("Error getting patient enrollment photos", error);
    throw error;
  }
};

// update a patient enrollment photo
export const updateEnrollmentPhoto = async (
  orgId: string,
  photoId: string,
  enrollmentId: string,
  patientPhoto: PatientPhoto
) => {
  try {
    const response = await velysInstance.updateEnrollmentPhoto(
      orgId,
      photoId,
      enrollmentId,
      patientPhoto
    );
    if (response.status === 200 && response.data) {
      return response.data;
    }
  } catch (error) {
    console.error("Error getting patient enrollment photos", error);
    throw error;
  }
};

// get org surgeons
export const getOrgSurgeons = async (orgId: string, cursor?: string) => {
  try {
    const response = await velysInstance.getSurgeons(orgId, cursor);
    return {
      data: response.data,
      cursor: response.headers["x-next-page-cursor"],
    };
  } catch (error) {
    console.error("Error getting org surgeons", error);
    throw error;
  }
};

//get org templates
export const getOrgTemplates = async (orgId: string) => {
  try {
    const response = await velysInstance.getTemplates(orgId);
    return response.data;
  } catch (error) {
    console.error("Error getting org templates", error);
    throw error;
  }
};

//get org template by ID
export const getOrgTemplateById = async (orgId: string, templateId: string) => {
  try {
    const response = await velysInstance.getTemplate(orgId, templateId);
    return response.data;
  } catch (error) {
    console.error("Error getting org template", error);
    throw error;
  }
};

// care plan upload request: pre_signed_url
export const createCareplanUpload = async (
  orgId: string,
  patientCarePlanUploadRequest: PatientCarePlanUploadRequest
) => {
  try {
    const response = await velysInstance.getTemplateUploadRequest(
      orgId,
      patientCarePlanUploadRequest
    );
    return response.data;
  } catch (error) {
    console.error("Error creating upload care plan request", error);
    throw error;
  }
};

// upload careplan w/ pre_signed_url
export const uploadCareplan = async (
  uploadURI: string,
  file: File,
  careplanUploadResponse: PatientCarePlanUploadResponse
) => {
  console.log(careplanUploadResponse);
  try {
    return await fileUploadInstance.put(uploadURI, file, {
      headers: {
        "Content-Type": file.type,
        ...careplanUploadResponse.additional_headers,
      },
    });
  } catch (error) {
    console.error("Error uploading careplan file:", error);
    throw error;
  }
};

// register/update patient
export const updatePatient = async (patient: Patient) => {
  try {
    const response = await velysInstance.updatePatient(patient);
    return response.data;
  } catch (error) {
    console.error("Error creating/updating patient", error);
    throw error;
  }
};

// update patient ToS with ID
export const updatePatientToS = async (
  patientId: string,
  patient: Patient
): Promise<Patient> => {
  try {
    const response = await velysInstance.updatePatientTos(patientId, patient);
    return response.data;
  } catch (error) {
    console.error("Error updating patient ToS", error);
    throw error;
  }
};

// get patient by Id
export const getPatientById = async (patientId: string) => {
  try {
    const response = await velysInstance.getPatient(patientId);
    return response.data;
  } catch (error) {
    console.error("Error getting patient", error);
    throw error;
  }
};

// get list of patients
export const getPatients = async () => {
  try {
    const response = await velysInstance.getPatients();
    return response.data;
  } catch (error) {
    console.error("Error getting list of patient", error);
    throw error;
  }
};

// create pending patient enrollment
export const createPendingPatientEnrollment = async (
  orgId: string,
  pendingPatientEnrollment: PendingPatientEnrollment
) => {
  try {
    const response = await velysInstance.createPendingPatientEnrollment(
      orgId,
      pendingPatientEnrollment
    );
    return response.data;
  } catch (error) {
    console.error("Error creating pending patient enrollment", error);
    throw error;
  }
};

// get pending patient enrollments
export const getPendingPatientEnrollments = async (
  orgId: string,
  q?: string,
  surgeonNpi?: string[],
  cursor?: string
) => {
  try {
    const response = await velysInstance.getPendingPatientEnrollments(
      orgId,
      q,
      surgeonNpi,
      cursor
    );
    return {
      data: response.data,
      cursor: response.headers["x-next-page-cursor"],
    };
  } catch (error) {
    console.error("Error getting pending patient enrollments", error);
    throw error;
  }
};

// get pending patient enrollment by id
export const getPendingPatientEnrollmentById = async (
  orgId: string,
  pendingPatientEnrollmentId: string
) => {
  try {
    const response = await velysInstance.getPendingPatientEnrollment(
      orgId,
      pendingPatientEnrollmentId
    );
    return response.data;
  } catch (error) {
    console.error("Error getting pending patient enrollment", error);
    throw error;
  }
};

// update pending enrollment
export const updatePendingPatientEnrollment = async (
  orgId: string,
  pendingPatientEnrollmentId: string,
  pendingPatientEnrollment: PendingPatientEnrollment
) => {
  try {
    const response = await velysInstance.updatePendingPatientEnrollment(
      orgId,
      pendingPatientEnrollmentId,
      pendingPatientEnrollment
    );
    return response.data;
  } catch (error) {
    console.error("Error updating pending patient enrollment", error);
    throw error;
  }
};

// delete pending patient enrollment
export const deletePendingPatientEnrollment = async (
  orgId: string,
  pendingPatientEnrollmentId: string
) => {
  try {
    const response = await velysInstance.deletePendingPatientEnrollment(
      orgId,
      pendingPatientEnrollmentId
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting pending patient enrollment", error);
    throw error;
  }
};

// create patient enrollment
export const createPatientEnrollment = async (
  orgId: string,
  createPatientEnrollmentRequest: CreatePatientEnrollmentRequest
) => {
  try {
    const response = await velysInstance.createPatientEnrollment(
      orgId,
      createPatientEnrollmentRequest
    );
    return response.data;
  } catch (error) {
    console.error("Error creating patient enrollment", error);
    throw error;
  }
};

// get patient enrollments
export const getPatientEnrollments = async (
  orgId: string,
  cursor?: string,
  q?: string,
  surgeonNpi?: string[]
) => {
  try {
    const response = await velysInstance.getPatientEnrollments(
      orgId,
      cursor,
      q,
      surgeonNpi
    );
    return {
      data: response.data,
      cursor: response.headers["x-next-page-cursor"],
    };
  } catch (error) {
    console.error("Error getting patient enrollments", error);
    throw error;
  }
};

// get patient enrollment by id
export const getPatientEnrollmentById = async (enrollmentId: string) => {
  try {
    const response = await velysInstance.getPatientEnrollment(enrollmentId);
    return response.data;
  } catch (error) {
    console.error("Error getting patient enrollment", error);
    throw error;
  }
};

// update patient enrollment
// export const updatePatientEnrollment = async (
//   orgId: string,
//   enrollmentId: string,
//   updatePatientEnrollmentRequest: UpdatePatientEnrollmentRequest
// ) => {
//   try {
//     const response = await velysInstance.updatePatientEnrollment(
//       orgId,
//       enrollmentId,
//       updatePatientEnrollmentRequest
//     );
//     return response.data;
//   } catch (error) {
//     console.error("Error updating patient enrollment", error);
//     throw error;
//   }
// };

// delete patient enrollment
export const deletePatientEnrollment = async (
  orgId: string,
  patientEnrollmentId: string
) => {
  try {
    const response = await velysInstance.deletePatientEnrollment(
      orgId,
      patientEnrollmentId
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting patient enrollment", error);
    throw error;
  }
};

// delete patient enrollment items
export const deletePatientEnrollmentItems = async (
  orgId: string,
  enrollmentId: string,
  bulkDeleteItemsRequest: BulkDeleteItemsRequest
) => {
  try {
    const response = await velysInstance.deletePatientEnrollmentItems(
      orgId,
      enrollmentId,
      bulkDeleteItemsRequest
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting patient enrollment items", error);
    throw error;
  }
};

// get enrollment action items
export const getEnrollmentActionItems = async (
  orgId: string,
  enrollmentId: string,
  status?: GetPatientEnrollmentActionsStatusEnum,
  data?: string
) => {
  try {
    const response = await velysInstance.getPatientEnrollmentActions(
      orgId,
      enrollmentId,
      status,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error getting enrollment action items", error);
    throw error;
  }
};

// get single enrollment action item
export const getEnrollmentActionItem = async (
  orgId: string,
  enrollmentId: string,
  actionId: string
) => {
  try {
    const response = await velysInstance.getPatientEnrollmentAction(
      orgId,
      enrollmentId,
      actionId
    );
    return response.data;
  } catch (error) {
    console.error("Error getting enrollment action item", error);
    throw error;
  }
};

// update enrollment action item status
export const updateEnrollmentActionItem = async (
  orgId: string,
  enrollmentId: string,
  actionId: string,
  actionUpdateStatusRequest: ActionUpdateStatusRequest
) => {
  try {
    const response = await velysInstance.updatePatientEnrollmentAction(
      orgId,
      enrollmentId,
      actionId,
      actionUpdateStatusRequest
    );
    return response.data;
  } catch (error) {
    console.error("Error updating enrollment action item status", error);
    throw error;
  }
};

// get predefined action items
export const getPredefinedActionItems = async (
  orgId: string,
  cursor?: string
) => {
  try {
    const response = await velysInstance.listPredefinedActions(orgId, cursor);
    return {
      data: response.data,
      cursor: response.headers["x-next-page-cursor"],
    };
  } catch (error) {
    console.error("Error getting predefined action items", error);
    throw error;
  }
};

// get single predefind action item by id
export const getPredefinedActionItemById = async (
  orgId: string,
  actionId: string
) => {
  try {
    const response = await velysInstance.getPredefinedAction(orgId, actionId);
    return response.data;
  } catch (error) {
    console.error("Error getting predefined action item", error);
    throw error;
  }
};

// create predefined enrollment action item
export const createPredefinedActionItem = async (
  orgId: string,
  createPredefinedActionRequest: PredefinedAction
) => {
  try {
    const response = await velysInstance.createPredefinedAction(
      orgId,
      createPredefinedActionRequest
    );
    return response.data;
  } catch (error) {
    console.error("Error create predefined action item", error);
    throw error;
  }
};

// update predefined action item
export const updatePredefinedAction = async (
  orgId: string,
  actionId: string,
  updatePredefinedActionRequest: PredefinedAction
) => {
  try {
    const response = await velysInstance.updatePredefinedAction(
      orgId,
      actionId,
      updatePredefinedActionRequest
    );
    return response.data;
  } catch (error) {
    console.error("Error updating predefined action item", error);
    throw error;
  }
};

// delete predefined action item
export const deletePredefinedAction = async (
  orgId: string,
  actionId: string
) => {
  try {
    const response = await velysInstance.deletePredefinedAction(
      orgId,
      actionId
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting predefined action item", error);
    throw error;
  }
};

// used for polling, bespoke template creation status
export const getBespokeTemplateById = async (
  orgId: string,
  templateId: string
) => {
  try {
    const response = await velysInstance.getTemplate(orgId, templateId);
    return response.data;
  } catch (error) {
    // If it's a 404 (template not ready yet), return null to continue polling
    if (error instanceof AxiosError) {
      if (error.response?.status === 404) {
        console.log("Template not ready yet (404)");
        return null;
      }
    }
    // For other errors, throw so polling stops
    console.error("Error getting org template", error);
    throw error;
  }
};

export const getAssessment = async (
  orgId: string,
  enrollmentId: string,
  assessmentId: string
) => {
  try {
    const response = await velysInstance.getAssessment(
      orgId,
      enrollmentId,
      assessmentId
    );
    return response.data;
  } catch (error) {
    console.error("Error getting assessment:", error);
    throw error;
  }
};

export const getAssessmentUrl = async (
  orgId: string,
  enrollmentId: string,
  assessmentId: string
) => {
  try {
    const response = await velysInstance.viewAssessment(
      orgId,
      enrollmentId,
      assessmentId
    );
    return response.data;
  } catch (error) {
    console.error("Error getting assessment URL:", error);
    throw error;
  }
};

// enrollment edit upload request: pre_signed_url
export const createEnrollmentEditUpload = async (
  orgId: string,
  enrollmentId: string
) => {
  try {
    const response =
      await velysInstance.organizationsOrgIdEnrollmentsEnrollmentIdEditsPost(
        orgId,
        enrollmentId
      );
    return response.data;
  } catch (error) {
    console.error("Error creating enrollment edit upload request", error);
    throw error;
  }
};

// upload enrollment edit w/ pre_signed_url
export const uploadEnrollmentEdit = async (
  uploadURI: string,
  file: File,
  enrollmentEditUploadResponse: EnrollmentEditUploadResponse
) => {
  try {
    return await fileUploadInstance.put(uploadURI, file, {
      headers: {
        "Content-Type": file.type,
        ...enrollmentEditUploadResponse.additional_headers,
      },
    });
  } catch (error) {
    console.error("Error uploading enrollment edit file:", error);
    throw error;
  }
};

export const getEnrollmentEditStatus = async (
  orgId: string,
  enrollmentId: string,
  editId: string
) => {
  try {
    const response =
      await velysInstance.organizationsOrgIdEnrollmentsEnrollmentIdEditsEditIdGet(
        orgId,
        enrollmentId,
        editId
      );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 404) {
        console.log("Enrollment edit not ready yet (404)");
        return null;
      }
    }
    console.error("Error getting enrollment edit status", error);
    throw error;
  }
};

// get single patient enrollment photo for polling status
export const getPatientEnrollmentPhotoById = async (
  orgId: string,
  photoId: string,
  enrollmentId: string
) => {
  try {
    const response = await velysInstance.getEnrollmentPhoto(
      orgId,
      photoId,
      enrollmentId
    );
    return response.data;
  } catch (error) {
    // If it's a 404 (template not ready yet), return null to continue polling
    if (error instanceof AxiosError) {
      if (error.response?.status === 404) {
        console.log("Photo not ready yet (404)");
        return null;
      }
    }
    // For other errors, throw so polling stops
    console.error("Error getting processing photo", error);
    throw error;
  }
};
